import { Box, IconButton, Typography } from '@mui/material';

import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { styled } from '@mui/system';

import useZenMode from '../../../hooks/useZenMode';
import { shadowBleed } from '../cardShadow';
import PagingRail from './PagingRail';
import {
  BORDER, CONTROL_TEXT, SANS_STACK, TEXT_SECONDARY,
} from '../tokens';

// A horizontal drag shorter than this is a tap, not a page turn.
const SWIPE_THRESHOLD = 50;

const PagingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  height: '100%',
  ...shadowBleed,
});

const PageInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1),
  flexShrink: 0,
  gap: theme.spacing(1),
  minHeight: '40px',
}));

const GridContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  ...shadowBleed,
});

// One treatment for both ends of the footer strip — the page indicator on the
// left, the counter on the right.
export const FooterText = styled(Typography)({
  fontFamily: SANS_STACK,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: TEXT_SECONDARY,
  whiteSpace: 'nowrap',
});

export const FooterDivider = styled(Typography)({
  color: BORDER,
  fontSize: 11,
});

/**
 * The "N things" figure at the right of the footer, or null when there is
 * nothing to count.
 */
export function formatCount(count, singular, plural) {
  if (!(count > 0)) return null;
  return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;
}

/**
 * The paged shell every entity grid sits in: side rails on desktop, a swipe on
 * touch, and the footer pager. The grid itself is `children` — this component
 * knows nothing about what it is paging over.
 *
 * `counter` is the entity's own stat line, pinned to the footer's right edge.
 * Supplying one also keeps the footer on a single page, since the count is
 * worth showing when the rails aren't; without one the footer appears only
 * when there is somewhere to page to.
 *
 * `hasContent` gates the whole shell, so an empty result renders the outer box
 * and nothing else — the callers each decide what counts as empty.
 */
export default function CardGrid(props) {
  const {
    hasContent, pageNumber, pagesCount, onNextPage, onPreviousPage, counter, children,
  } = props;

  const hasPagination = pagesCount > 1;
  const zenMode = useZenMode();

  const touchStartX = useRef(null);
  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const onTouchEnd = useCallback((e) => {
    if (touchStartX.current == null || !hasPagination) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0 && pageNumber < pagesCount) onNextPage();
    else if (dx > 0 && pageNumber > 1) onPreviousPage();
  }, [hasPagination, pageNumber, pagesCount, onNextPage, onPreviousPage]);

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column', ...shadowBleed,
    }}>
      {hasContent && (
        <PagingContainer onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {hasPagination && !zenMode && (
            <PagingRail
              side="left"
              disabled={pageNumber === 1}
              onClick={onPreviousPage}
            />
          )}
          <GridContainer>
            {children}
            {!zenMode && (hasPagination || counter) && (
              <PageInfo sx={{ position: 'relative', justifyContent: 'center' }}>
                {hasPagination && (
                  <IconButton
                    size="small"
                    disabled={pageNumber === 1}
                    onClick={onPreviousPage}
                    aria-label="Previous page"
                    sx={{ color: CONTROL_TEXT, display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                )}
                {hasPagination && (
                  <FooterText sx={{ display: { xs: 'block', sm: 'none' } }}>
                    {pageNumber}
                  </FooterText>
                )}
                {hasPagination && (
                  <IconButton
                    size="small"
                    disabled={pageNumber === pagesCount}
                    onClick={onNextPage}
                    aria-label="Next page"
                    sx={{ color: CONTROL_TEXT, display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                )}
                {hasPagination && (
                  <FooterText sx={{ position: 'absolute', left: 16, display: { xs: 'none', sm: 'block' } }}>
                    {`Page ${pageNumber} of ${pagesCount}`}
                  </FooterText>
                )}
                {counter && (
                  <Box sx={{
                    position: 'absolute', right: 16, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
                  }}>
                    {counter}
                  </Box>
                )}
              </PageInfo>
            )}
          </GridContainer>
          {hasPagination && !zenMode && (
            <PagingRail
              side="right"
              disabled={pageNumber === pagesCount}
              onClick={onNextPage}
            />
          )}
        </PagingContainer>
      )}
    </Box>
  );
}

CardGrid.propTypes = {
  hasContent: PropTypes.bool,
  pageNumber: PropTypes.number,
  pagesCount: PropTypes.number,
  onNextPage: PropTypes.func.isRequired,
  onPreviousPage: PropTypes.func.isRequired,
  counter: PropTypes.node,
  children: PropTypes.node,
};

CardGrid.defaultProps = {
  hasContent: false,
  pageNumber: 1,
  pagesCount: 0,
  counter: null,
  children: null,
};
